import type {
  BlockLocatorDetails,
  ContextData,
  ContextItem
} from '@epilot/journey-logic-commons'
import { unique } from 'radashi'

import type {
  BaseEntity,
  EntityClient,
  EntityItem,
  EntitySlug,
  ExtendedCustomerPortalClient
} from '../clients'

// Right now this is only used to fetch entities for Lookup Block
export const fetchBlockEntities = (
  client: EntityClient | ExtendedCustomerPortalClient,
  slug: string,
  attributes: string[],
  isPortal
) => {
  if (isPortal) {
    return fetchPortalEntities(
      client as ExtendedCustomerPortalClient,
      slug,
      attributes
    )
  } else {
    return fetch360Entities(
      client as EntityClient,
      `_schema:${slug}`,
      attributes,
      20
    )
  }
}

// Right now this is only used to fetch entities for context parameters
export const fetchContextEntities = async (
  contextSchema: ContextItem[],
  client: EntityClient | ExtendedCustomerPortalClient,
  attributes: Record<string, string[]>,
  contextValues: ContextData,
  isPortal: boolean
) => {
  const entitiesData: Record<string, BaseEntity> = {}

  if (contextSchema.length > 0 && client) {
    for (const c of contextSchema) {
      const key = c.paramKey.toLowerCase()
      const slug = c.type
      const entityAttributes = attributes[key] || []

      // String contextSchema is not expected to be an entity, so we skip them
      if (slug === 'String') {
        continue
      }
      // Contract is a special case because it works in tandem with the Meter Reading block
      // For this reason we have to make sure that these attributes are always added
      if (slug === 'contract') {
        entityAttributes.push('customer')
        entityAttributes.push('status')
        entityAttributes.push('contract_name')
      }
      const entityId = contextValues[key]

      if (entityId && c.shouldLoadEntity === true) {
        await fetchSingleEntity(
          isPortal,
          client,
          entityAttributes,
          slug,
          entityId
        ).then((result) => {
          entitiesData[key] = result
        })
      }
    }
  }

  return entitiesData
}

// Context entities expect to fetch entities one by one.
// This function make use of the wrapped api calls, but limit the result to a single entity.
const fetchSingleEntity = (
  isPortal: boolean,
  client: EntityClient | ExtendedCustomerPortalClient,
  attributes: string[],
  slug: string,
  entityId: string
): Promise<EntityItem> => {
  if (isPortal) {
    return fetchPortalEntities(
      client as ExtendedCustomerPortalClient,
      slug,
      attributes
    )
      .then((res) => {
        const results = res?.data?.results || []
        const result = results.filter((res) => res._id === entityId)[0]

        if (result) {
          return result
        } else {
          throw new Error(
            `Provided id did not retrieve any entity: ${entityId}`
          )
        }
      })
      .catch((e) => {
        throw new Error(`Entity ${entityId} failed to load`, e)
      })
  } else {
    return fetch360Entities(
      client as EntityClient,
      `_id:${entityId} AND _schema:${slug}`,
      attributes
    )
      .then((res) => {
        const results = res?.data?.results || []

        if (results[0]) {
          return results[0]
        } else {
          throw new Error(
            `Provided id did not retrieve any entity: ${entityId}`
          )
        }
      })
      .catch((e) => {
        throw new Error(`Entity ${entityId} failed to load`, e)
      })
  }
}

// The following 2 functions wrap client calls into functions for easy reutilization
// With this we remove what used to be duplicated code accross context and lookup
const fetchPortalEntities = (
  client: ExtendedCustomerPortalClient,
  slug: string,
  attributes: string[]
) => {
  return (client as ExtendedCustomerPortalClient)
    ?.searchPortalUserEntities(null, {
      slug: slug as EntitySlug,
      sort: '_created_at:asc',
      fields: unique(['_id', '_schema', ...attributes]),
      hydrate: true,
      size: 250
    })
    .then((res) => {
      return res
    })
    .catch((e) => {
      throw new Error(`Fetching portal entities failed`, e)
    })
}

const fetch360Entities = (
  client: EntityClient,
  q: string,
  attributes: string[],
  size?: number
) => {
  return (client as EntityClient)
    ?.searchEntities(null, {
      q: q,
      fields: unique(['_id', ...attributes]),
      sort: '_created_at:asc',
      hydrate: true,
      size: size || 250
    } as never)
    .then((res) => {
      return res
    })
    .catch((e) => {
      throw new Error(`Fetching 360 entities failed`, e)
    })
}

// Infers which attributes we need to fetch for an entity by checking what is actually being used
// This logic was already being used for context fetches... but we updated to also use with lookup blocks.
// For Lookup block we used to require the user to select needed attributes, but this was confusing and hard to explain
export const getNeededAttributes = (
  lookupBlocks: BlockLocatorDetails[],
  attributeBlocks: BlockLocatorDetails[]
) => {
  const neededAttributes: Record<string, string[]> = {}

  attributeBlocks.forEach((attributeBlock) => {
    const options = attributeBlock.uischema.options || {}
    const key: string =
      options.relatedContextEntity?.toLowerCase() ||
      options.relatedLookupBlock?.toLowerCase()

    if (neededAttributes[key] === undefined) {
      neededAttributes[key] = []
    }
    neededAttributes[key].push(options.attributeName)
  })

  lookupBlocks.forEach((lookupBlock) => {
    const options = lookupBlock.uischema.options || {}
    const key: string =
      options.relatedContextEntity?.toLowerCase() ||
      options.relatedLookupBlock?.toLowerCase()

    if (neededAttributes[key] === undefined) {
      neededAttributes[key] = []
    }
    neededAttributes[key].push(
      options.relatedContextAttribute || options.relatedLookupAttribute
    )
  })

  return neededAttributes
}
